import { estDemo, type DemoEntrees } from "@hakili/app-shell";
import { t } from "@hakili/i18n";

import { PERSONAS, PERSONA_DEFINITIONS, type Persona } from "./personas.js";

/**
 * `P0-UIK-018` and `WF-BNK-01`: the bank's persona switcher, behind the flag.
 *
 * 03, 3.0's decision is explicit: *"the persona switcher at the bottom of the
 * shell is a demonstration device. In production the persona is derived from
 * the authenticated user's role inside the bank organisation, and the switcher
 * only exists behind the `demo` flag."*
 *
 * **Danger sentence, and this one is about money.** The two personas are not
 * two layouts. `chef` carries `/decisions` and `/politique` , the queue where
 * offers are validated and the credit policy that governs them , and `analyste`
 * carries neither. A switcher in production is an analyst one tap from the
 * screens that decide whether a borrower is funded, inside a PARTNER BANK's
 * deployment, which is the one tenant whose staff Hakili does not employ.
 *
 * The rights do not move with the persona: 6.1.8 answers to the JWT, so the
 * chef's screens render for an analyst and every call behind them is refused.
 * That makes the device demonstrable rather than dangerous , but only because
 * the server holds the line, and only while this component is absent from
 * production builds.
 */

export interface PersonaSwitcherProps {
  readonly demo: DemoEntrees;
  readonly persona: Persona;
  readonly choisir: (persona: Persona) => void;
}

export function PersonaSwitcher({ demo, persona, choisir }: PersonaSwitcherProps): React.JSX.Element | null {
  if (!estDemo(demo)) return null;

  return (
    <nav
      aria-label={t("bank.demo.persona")}
      data-testid="persona-switcher"
      className="flex gap-2 border-t border-gray-200 bg-white p-3"
    >
      {PERSONAS.map((candidate) => (
        <button
          key={candidate}
          type="button"
          aria-current={candidate === persona ? "true" : undefined}
          onClick={() => {
            choisir(candidate);
          }}
          className={`rounded-full px-3 py-1 text-xs ${
            candidate === persona ? "bg-indigo-600 text-white" : "bg-gray-50 text-gray-500"
          }`}
        >
          {t(PERSONA_DEFINITIONS[candidate].labelKey)}
        </button>
      ))}
    </nav>
  );
}
